/**
 * Shared DataGrid toolbar for editable and overview grids.
 *
 * Legacy DisplayUnits-style controls (add / sort / columns / filter / density)
 * default ON so existing callers stay stable. Overview controls (quick filter,
 * reset layout, Excel export, reload) default OFF and opt in.
 */
import React, { useCallback, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SortIcon from "@mui/icons-material/Sort";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import {
  exportGridToExcel,
  ExportGridToExcelOptions,
} from "./ratDataGridExcelExport.util";

export type RATDataGridToolbarExcelExportConfig = Omit<
  ExportGridToExcelOptions,
  "getFileName"
> & {
  readonly getFileName: () => string;
};

export interface RATDataGridToolbarProps {
  // --- Editable-grid controls (default ON) ---
  showAddButton?: boolean;
  addButtonText?: string;
  handleAddRow?: () => void;
  showApplySortButton?: boolean;
  handleApplySort?: () => void;
  showClearSortButton?: boolean;
  handleClearSort?: () => void;
  showColumnsButton?: boolean;
  showFilterButton?: boolean;
  showDensityButton?: boolean;

  // --- Overview-grid controls (default OFF) ---
  showQuickFilter?: boolean;
  quickFilterPlaceholder?: string;
  quickFilterDebounceMs?: number;
  /** Usually wired from useGridStatePersistence.resetGridState via RATDataGrid. */
  showResetLayout?: boolean;
  onResetLayout?: () => void;
  showExcelExport?: boolean;
  excelExportConfig?: RATDataGridToolbarExcelExportConfig;
  showReloadButton?: boolean;
  onReloadData?: () => void;
  isRefreshing?: boolean;
}

export const RATDataGridToolbar: React.FC<RATDataGridToolbarProps> = ({
  showAddButton = true,
  addButtonText = "Add row",
  handleAddRow = () => {
    return;
  },
  showApplySortButton = true,
  handleApplySort = () => {
    return;
  },
  showClearSortButton = true,
  handleClearSort = () => {
    return;
  },
  showColumnsButton = true,
  showFilterButton = true,
  showDensityButton = true,
  showQuickFilter = false,
  quickFilterPlaceholder,
  quickFilterDebounceMs = 300,
  showResetLayout = false,
  onResetLayout,
  showExcelExport = false,
  excelExportConfig,
  showReloadButton = false,
  onReloadData,
  isRefreshing = false,
}) => {
  // Must run inside a DataGridPro slot so the export API is available.
  const apiRef = useGridApiContext();
  const [isExporting, setIsExporting] = useState(false);

  const handleExportToExcel = useCallback(async () => {
    if (!excelExportConfig) {
      return;
    }

    setIsExporting(true);
    try {
      await exportGridToExcel(apiRef, excelExportConfig);
    } finally {
      setIsExporting(false);
    }
  }, [apiRef, excelExportConfig]);

  return (
    <GridToolbarContainer>
      {showQuickFilter && (
        <GridToolbarQuickFilter
          debounceMs={quickFilterDebounceMs}
          placeholder={quickFilterPlaceholder}
        />
      )}
      {showAddButton && (
        <Button size="small" onClick={handleAddRow}>
          <AddCircleOutlineIcon />
          {addButtonText}
        </Button>
      )}
      {showApplySortButton && (
        <Button size="small" onClick={handleApplySort}>
          <SortIcon />
          Apply Sort
        </Button>
      )}
      {showClearSortButton && (
        <Button size="small" onClick={handleClearSort}>
          <ClearIcon />
          Clear Sort
        </Button>
      )}
      {showFilterButton && <GridToolbarFilterButton />}
      {showColumnsButton && <GridToolbarColumnsButton />}
      {showDensityButton && <GridToolbarDensitySelector />}
      {showResetLayout && (
        <Button
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={onResetLayout}
          disabled={!onResetLayout}
        >
          Reset Layout
        </Button>
      )}
      {showExcelExport && (
        <>
          <Box sx={{ width: 8 }} />
          <Button
            size="small"
            startIcon={
              isExporting ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                <TableChartOutlinedIcon />
              )
            }
            onClick={handleExportToExcel}
            disabled={isExporting || !excelExportConfig}
          >
            Export to Excel
          </Button>
        </>
      )}
      {/* ml:auto pins Reload to the trailing edge of the toolbar. */}
      {showReloadButton && (
        <Button
          size="small"
          sx={{ ml: "auto" }}
          startIcon={
            isRefreshing ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              <RefreshIcon />
            )
          }
          onClick={onReloadData}
          disabled={!onReloadData || isRefreshing}
        >
          Reload
        </Button>
      )}
    </GridToolbarContainer>
  );
};

export default RATDataGridToolbar;
